import React, { useState, useCallback, useEffect, useRef } from 'react';
import { WrapGenerator } from '../utils/poker';
import Card from './Card';
import { WrapResult } from '../types/poker';
import { PulsatingButton } from './ui/pulsating-button';
import AppMenu from './app/AppMenu';
import OutsInput from './OutsInput';
import LeaderboardModal from './LeaderboardModal';
import {
  startCompetitionRun,
  submitCompetitionScore,
  getLeaderboard,
  LeaderboardEntry,
} from '../utils/apiClient';
import './WrapGame.css';

export type GameMode = 'competitive' | 'practice' | 'chill';

const COMPETITIVE_DURATION_SECONDS = 5 * 60;
const POINTS_PER_CORRECT = 1000;

interface PracticeFeedback {
  totalGuess: number;
  nutGuess: number;
  correct: WrapResult;
  totalCorrect: boolean;
  nutCorrect: boolean;
}

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const WrapGame = () => {
  // Create initial generator
  const initialGenerator = new WrapGenerator();

  const [mode, setMode] = useState<GameMode>('chill');

  const [board, setBoard] = useState(initialGenerator.getBoard());
  const [holeCards, setHoleCards] = useState(initialGenerator.getHoleCards());
  const [result, setResult] = useState<WrapResult | null>(null);
  const [generator, setGenerator] = useState(initialGenerator);
  const [handId, setHandId] = useState(0);

  // Chill-mode timing stats
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [times, setTimes] = useState<number[]>([]);
  const [averageTime, setAverageTime] = useState<number | null>(null);
  const [lastTime, setLastTime] = useState<number | null>(null);

  // Practice-mode feedback
  const [practiceFeedback, setPracticeFeedback] = useState<PracticeFeedback | null>(null);

  // Competitive-mode state
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(COMPETITIVE_DURATION_SECONDS);
  const [competitiveActive, setCompetitiveActive] = useState(false);
  const [finalScore, setFinalScore] = useState<number | null>(null);

  // Leaderboard / splash
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState<string | null>(null);

  const scoreRef = useRef(score);
  scoreRef.current = score;

  // Single-use session token for the current competitive run, issued by the
  // server when the run starts and submitted with the final score.
  const sessionTokenRef = useRef<string | null>(null);

  // Initialize timer on hand change (chill stat tracking)
  useEffect(() => {
    setStartTime(Date.now());
  }, [handId]);

  const generateNewHand = useCallback(() => {
    const newGenerator = new WrapGenerator();
    setBoard(newGenerator.getBoard());
    setHoleCards(newGenerator.getHoleCards());
    setResult(null);
    setPracticeFeedback(null);
    setGenerator(newGenerator);
    setHandId(prev => prev + 1);
  }, []);

  // --- Chill mode (reveal flow) ---
  const showResult = useCallback(() => {
    const endTime = Date.now();
    const timeTaken = (endTime - startTime) / 1000; // seconds

    // Only record times between 0.1 and 300 seconds (5 minutes)
    if (timeTaken >= 0.1 && timeTaken <= 300) {
      setLastTime(timeTaken);
      setTimes(prevTimes => {
        const newTimes = [...prevTimes, timeTaken].slice(-10);
        const avg = newTimes.reduce((a, b) => a + b, 0) / newTimes.length;
        setAverageTime(avg);
        return newTimes;
      });
    }

    setResult(generator.getResult());
  }, [generator, startTime]);

  // --- Practice mode ---
  const handlePracticeSubmit = useCallback(
    (totalGuess: number, nutGuess: number) => {
      const correct = generator.getResult();
      setPracticeFeedback({
        totalGuess,
        nutGuess,
        correct,
        totalCorrect: totalGuess === correct.outs,
        nutCorrect: nutGuess === correct.nutOuts,
      });
    },
    [generator]
  );

  // --- Leaderboard fetching ---
  const refreshLeaderboard = useCallback(async () => {
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    try {
      const entries = await getLeaderboard();
      setLeaderboard(entries);
    } catch (err: any) {
      setLeaderboardError('Could not load the leaderboard.');
    } finally {
      setLeaderboardLoading(false);
    }
  }, []);

  // --- Competitive mode ---
  const startCompetitiveRun = useCallback(async () => {
    // Obtain a single-use session token from the server before the run begins;
    // it's required to submit the final score. Bail out if it can't be issued.
    setLeaderboardError(null);
    try {
      const session = await startCompetitionRun();
      sessionTokenRef.current = session.session_token;
    } catch (err: any) {
      setLeaderboardError('Could not start a competitive run. Please try again.');
      return;
    }

    setScore(0);
    setTimeLeft(COMPETITIVE_DURATION_SECONDS);
    setFinalScore(null);
    setShowLeaderboard(false);
    setCompetitiveActive(true);
    generateNewHand();
  }, [generateNewHand]);

  const handleCompetitiveSubmit = useCallback(
    (totalGuess: number, nutGuess: number) => {
      if (!competitiveActive) return;
      const correct = generator.getResult();
      let gained = 0;
      if (totalGuess === correct.outs) gained += POINTS_PER_CORRECT;
      if (nutGuess === correct.nutOuts) gained += POINTS_PER_CORRECT;
      if (gained > 0) setScore(s => s + gained);
      generateNewHand();
    },
    [competitiveActive, generator, generateNewHand]
  );

  // Countdown tick
  useEffect(() => {
    if (!competitiveActive) return;
    const intervalId = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [competitiveActive]);

  // End of run: lock inputs, show final score, persist + refresh leaderboard
  useEffect(() => {
    if (!competitiveActive || timeLeft > 0) return;

    const endRun = async () => {
      const runScore = scoreRef.current;
      const sessionToken = sessionTokenRef.current;
      setCompetitiveActive(false);
      setFinalScore(runScore);
      setShowLeaderboard(true);
      setLeaderboardLoading(true);
      setLeaderboardError(null);
      try {
        if (!sessionToken) {
          throw new Error('Missing competitive run session token');
        }
        await submitCompetitionScore(runScore, sessionToken);
        const entries = await getLeaderboard();
        setLeaderboard(entries);
      } catch (err: any) {
        setLeaderboardError('Could not submit your score.');
      } finally {
        // A session token is single-use; clear it so a stale one is never reused.
        sessionTokenRef.current = null;
        setLeaderboardLoading(false);
      }
    };

    endRun();
  }, [competitiveActive, timeLeft]);

  // --- Mode switching ---
  const handleSelectMode = useCallback(
    (next: GameMode) => {
      if (next === mode) return;
      setMode(next);
      setCompetitiveActive(false);
      setFinalScore(null);
      setPracticeFeedback(null);
      setResult(null);
      generateNewHand();

      if (next === 'competitive') {
        // Show leaderboard splash on entry.
        setShowLeaderboard(true);
        refreshLeaderboard();
      } else {
        setShowLeaderboard(false);
      }
    },
    [mode, generateNewHand, refreshLeaderboard]
  );

  const exitToChill = useCallback(() => {
    setShowLeaderboard(false);
    handleSelectMode('chill');
  }, [handleSelectMode]);

  const cardRow = (cards: typeof board, prefix: string) => (
    <div
      style={{
        width: '105.26%',
        marginLeft: '-2.63%',
        marginRight: '-2.63%',
        display: 'flex',
        justifyContent: 'center',
        gap: 'min(6px, 1vw)',
        marginBottom: 'min(12px, 2.5vw)',
        flexWrap: 'wrap',
        transform: 'scale(0.95)',
        transformOrigin: 'center center',
      }}
    >
      {cards.map((card, index) => (
        <Card key={`${prefix}-${handId}-${index}-${card.value}-${card.suit}`} card={card} />
      ))}
    </div>
  );

  const modeButton = (value: GameMode, label: string) => {
    const isActive = mode === value;
    return (
      <button
        onClick={() => handleSelectMode(value)}
        style={{
          padding: 'min(8px, 1.6vw) min(16px, 3.2vw)',
          fontSize: 'min(0.95rem, 3.8vw)',
          backgroundColor: isActive ? '#2196F3' : 'transparent',
          color: isActive ? 'white' : '#aaa',
          border: 'none',
          borderRadius: 'min(8px, 1.6vw)',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          fontWeight: isActive ? 600 : 400,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      style={{
        maxWidth: 'min(800px, 95vw)',
        margin: '0 auto',
        padding: 'min(20px, 4vw)',
        textAlign: 'center',
      }}
    >
      {/* Mode switch sits where the title used to be, with the menu on the right */}
      <div className="flex justify-between items-center mb-4">
        <div
          style={{
            display: 'inline-flex',
            gap: 'min(4px, 0.8vw)',
            padding: 'min(4px, 0.8vw)',
            backgroundColor: '#1e1e1e',
            borderRadius: 'min(10px, 2vw)',
            border: '1px solid #333',
          }}
        >
          {modeButton('competitive', 'Competitive')}
          {modeButton('practice', 'Practice')}
          {modeButton('chill', 'Chill')}
        </div>
        <AppMenu />
      </div>

      {/* Competitive HUD */}
      {mode === 'competitive' && competitiveActive && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'min(40px, 8vw)',
            marginBottom: 'min(20px, 4vw)',
            fontSize: 'min(1.3rem, 5.2vw)',
          }}
        >
          <span>
            ⏱ <strong style={{ color: timeLeft <= 30 ? '#f87171' : 'inherit' }}>{formatTime(timeLeft)}</strong>
          </span>
          <span>
            🎯 <strong style={{ color: '#3b82f6' }}>{score.toLocaleString()}</strong>
          </span>
        </div>
      )}

      <div style={{ marginBottom: 'min(30px, 6vw)' }}>
        <h2
          style={{
            fontSize: 'min(1.2rem, 5vw)',
            color: '#666666',
            fontWeight: 'normal',
            marginBottom: 'min(6px, 1.5vw)',
          }}
        >
          Board
        </h2>
        {cardRow(board, 'board')}

        <h2
          style={{
            fontSize: 'min(1.2rem, 5vw)',
            color: '#666666',
            fontWeight: 'normal',
            marginBottom: 'min(6px, 1.5vw)',
          }}
        >
          Hole Cards
        </h2>
        {cardRow(holeCards, 'hole')}

        {/* ---- Chill mode: reveal flow (unchanged) ---- */}
        {mode === 'chill' && (
          <div style={{ marginBottom: 'min(40px, 8vw)' }}>
            <div
              style={{
                fontSize: 'min(1.2rem, 5vw)',
                minHeight: '3em',
                overflow: 'hidden',
                marginBottom: 'min(20px, 4vw)',
              }}
            >
              {result && (
                <div className="slide-in">
                  <p>
                    <strong className="text-blue-500">Total Outs: {result.outs}</strong>
                    <br />
                    <strong className="text-green-500">Nut Outs: {result.nutOuts}</strong>
                    {(averageTime !== null || lastTime !== null) && (
                      <>
                        <br />
                        <span
                          style={{
                            color: '#666666',
                            fontSize: 'min(1rem, 4vw)',
                            display: 'block',
                            marginTop: 'min(10px, 2vw)',
                          }}
                        >
                          {lastTime !== null && `Last Time: ${lastTime.toFixed(1)}s`}
                          {lastTime !== null && averageTime !== null && ' • '}
                          {averageTime !== null && `Avg Time: ${averageTime.toFixed(1)}s`}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 'min(15px, 3vw)', justifyContent: 'center' }}>
              {!result && (
                <PulsatingButton
                  onClick={showResult}
                  style={{
                    padding: 'min(12px, 2.4vw) min(24px, 4.8vw)',
                    fontSize: 'min(1.2rem, 4.8vw)',
                  }}
                >
                  Show Answer
                </PulsatingButton>
              )}

              <button
                onClick={generateNewHand}
                style={{
                  padding: 'min(12px, 2.4vw) min(24px, 4.8vw)',
                  fontSize: 'min(1.2rem, 4.8vw)',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'min(6px, 1.2vw)',
                  cursor: 'pointer',
                }}
              >
                Next Hand
              </button>
            </div>
          </div>
        )}

        {/* ---- Practice mode ---- */}
        {mode === 'practice' && (
          <div style={{ marginBottom: 'min(40px, 8vw)' }}>
            {!practiceFeedback ? (
              <OutsInput key={handId} onSubmit={handlePracticeSubmit} submitLabel="Submit" />
            ) : (
              <div className="slide-in">
                <div style={{ fontSize: 'min(1.1rem, 4.4vw)', marginBottom: 'min(20px, 4vw)' }}>
                  <p style={{ color: practiceFeedback.totalCorrect ? '#22c55e' : '#f87171' }}>
                    Total Outs: you guessed {practiceFeedback.totalGuess} —{' '}
                    {practiceFeedback.totalCorrect
                      ? 'Correct!'
                      : `Answer: ${practiceFeedback.correct.outs}`}
                  </p>
                  <p style={{ color: practiceFeedback.nutCorrect ? '#22c55e' : '#f87171' }}>
                    Nut Outs: you guessed {practiceFeedback.nutGuess} —{' '}
                    {practiceFeedback.nutCorrect
                      ? 'Correct!'
                      : `Answer: ${practiceFeedback.correct.nutOuts}`}
                  </p>
                </div>
                <button
                  onClick={generateNewHand}
                  style={{
                    padding: 'min(12px, 2.4vw) min(24px, 4.8vw)',
                    fontSize: 'min(1.2rem, 4.8vw)',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'min(6px, 1.2vw)',
                    cursor: 'pointer',
                  }}
                >
                  Next Hand
                </button>
              </div>
            )}
          </div>
        )}

        {/* ---- Competitive mode ---- */}
        {mode === 'competitive' && (
          <div style={{ marginBottom: 'min(40px, 8vw)' }}>
            {competitiveActive ? (
              <OutsInput key={handId} onSubmit={handleCompetitiveSubmit} submitLabel="Submit" />
            ) : (
              !showLeaderboard && (
                <button
                  onClick={() => {
                    setShowLeaderboard(true);
                    refreshLeaderboard();
                  }}
                  style={{
                    padding: 'min(12px, 2.4vw) min(24px, 4.8vw)',
                    fontSize: 'min(1.2rem, 4.8vw)',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'min(6px, 1.2vw)',
                    cursor: 'pointer',
                  }}
                >
                  View Leaderboard
                </button>
              )
            )}
          </div>
        )}
      </div>

      {showLeaderboard && (
        <LeaderboardModal
          entries={leaderboard}
          loading={leaderboardLoading}
          error={leaderboardError}
          finalScore={finalScore}
          primaryLabel={finalScore !== null ? 'Play Again' : 'Start'}
          onPrimary={startCompetitiveRun}
          secondaryLabel="Exit"
          onSecondary={exitToChill}
        />
      )}
    </div>
  );
};

export default WrapGame;
